import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Bell, Check } from "lucide-react";
import { useAuth } from "./AuthProvider";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Floating notification bell — top-right on every screen for authed users.
 * Polls /api/notifications every 60s to keep unread_count fresh. Opens a
 * dropdown listing recent items; clicking an item marks it read + navigates.
 */
export const NotificationBell = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const nav = useNavigate();
  const wrapRef = useRef(null);

  const fetchNotifs = async () => {
    try {
      const r = await fetch(`${API}/api/notifications`, { credentials: "include" });
      if (!r.ok) return;
      const d = await r.json();
      setItems(d.rows || []);
      setUnread(d.unread_count || 0);
    } catch {}
  };

  useEffect(() => {
    if (!user) { setItems([]); setUnread(0); return; }
    fetchNotifs();
    const t = setInterval(fetchNotifs, 60000);
    return () => clearInterval(t);
  }, [user]);

  useEffect(() => {
    const onClick = (e) => {
      if (open && wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!user) return null;

  const openItem = async (n) => {
    setOpen(false);
    try {
      await fetch(`${API}/api/notifications/${n.id}/read`, {
        method: "POST", credentials: "include",
      });
    } catch {}
    setUnread((u) => Math.max(0, u - (n.read ? 0 : 1)));
    if (n.url) nav(n.url);
  };

  const markAllRead = async () => {
    try {
      await fetch(`${API}/api/notifications/read-all`, {
        method: "POST", credentials: "include",
      });
    } catch {}
    setUnread(0);
    setItems((it) => it.map((n) => ({ ...n, read: true })));
  };

  return (
    <div ref={wrapRef} className="fixed top-3 right-16 z-50">
      <button
        data-testid="notif-bell"
        onClick={() => setOpen((o) => !o)}
        className="relative w-9 h-9 rounded-full bg-[#16141D]/90 border border-white/15 flex items-center justify-center hover:border-[#FF107A]/60 transition-colors"
        aria-label="Notifications"
      >
        <Bell size={16} className="text-[#FDFBF7]" />
        {unread > 0 && (
          <span
            data-testid="notif-unread-count"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF107A] text-[#FDFBF7] text-[10px] font-bold flex items-center justify-center"
            style={{ boxShadow: "0 0 8px #FF107A" }}
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            data-testid="notif-dropdown"
            className="absolute top-11 right-0 w-80 max-h-[70vh] overflow-y-auto rounded-2xl border border-white/15 bg-[#0A090D]/95 backdrop-blur-xl shadow-2xl"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#FFD700]">
                Notifications
              </div>
              {unread > 0 && (
                <button
                  data-testid="notif-mark-all"
                  onClick={markAllRead}
                  className="text-[10px] uppercase tracking-[0.15em] text-[#00E5FF] hover:text-[#FDFBF7]"
                >
                  Mark all read
                </button>
              )}
            </div>
            {items.length === 0 ? (
              <div data-testid="notif-empty" className="px-4 py-8 text-center text-sm text-[#FDFBF7]/50">
                You're all caught up.
              </div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  data-testid={`notif-item-${n.id}`}
                  onClick={() => openItem(n)}
                  className={`w-full text-left px-4 py-3 border-b border-white/5 last:border-b-0 hover:bg-[#FF107A]/10 transition-colors ${
                    n.read ? "opacity-70" : ""
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <div className={`mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                      n.read ? "bg-white/20" : "bg-[#FF107A]"
                    }`} style={n.read ? {} : { boxShadow: "0 0 6px #FF107A" }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#FDFBF7] truncate">{n.title}</div>
                      {n.body && (
                        <div className="text-xs text-[#FDFBF7]/60 mt-0.5 line-clamp-2">{n.body}</div>
                      )}
                    </div>
                    {n.read && <Check size={12} className="text-[#FDFBF7]/40 mt-1" />}
                  </div>
                </button>
              ))
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
